import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the Theme Context
const ThemeContext = createContext();

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('app_theme');
    return savedTheme || 'dark';
  });

  // Save theme preference to localStorage AND apply to document
  useEffect(() => {
    localStorage.setItem('app_theme', theme);
    
    // Apply theme class to document body and html
    const root = document.documentElement;
    const body = document.body;
    
    if (theme === 'dark') {
      root.classList.remove('light-mode');
      root.classList.add('dark-mode');
      body.classList.remove('light-mode');
      body.classList.add('dark-mode');
      // Set data attribute for CSS selectors
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark-mode');
      root.classList.add('light-mode');
      body.classList.remove('dark-mode');
      body.classList.add('light-mode');
      // Set data attribute for CSS selectors
      root.setAttribute('data-theme', 'light');
    }
  }, [theme]);

  // Toggle between dark and light mode
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  // Set a specific theme
  const setSpecificTheme = (newTheme) => {
    if (newTheme === 'dark' || newTheme === 'light') {
      setTheme(newTheme);
    }
  };

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setTheme: setSpecificTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Default theme context value for fallback
const defaultThemeContext = {
  theme: 'dark',
  isDark: true,
  isLight: false,
  toggleTheme: () => {},
  setTheme: () => {},
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  // Return default context if not within ThemeProvider (graceful fallback)
  if (!context) {
    console.warn('useTheme: No ThemeProvider found, using default dark theme');
    return defaultThemeContext;
  }
  return context;
};

// For class components - HOC wrapper
export const withTheme = (WrappedComponent) => {
  return function WithThemeComponent(props) {
    // Use context with fallback
    const context = useContext(ThemeContext);
    // Prefer explicitly passed themeContext (important for Modal portals where context may not propagate)
    const themeContext = props.themeContext || context || defaultThemeContext;
    return <WrappedComponent {...props} themeContext={themeContext} />;
  };
};

// Export the context for direct use if needed
export default ThemeContext;
